import { ToolbarSwitcher } from "@/components/toolbar-switcher";
import { useApiError } from "@/hooks";
import {
	DataTable,
	useDataTableRowSelection,
	useServerTableState,
} from "@st-marketplace/ui";
import { useState } from "react";
import OrdersBulkAction from "./OrdersBulkAction";
import { OrdersFilterForm } from "./OrdersFilterForm";
import { useOrdersConfig } from "./hooks/useOrdersConfig";
import type { OrderRow } from "./hooks/useOrdersConfig";
import type { OrdersFilterValues, OrdersSchemaType } from "./types";

type OrdersContentProps = {
	schemaType: OrdersSchemaType;
	buyerId?: string;
	sellerId?: string;
	showFilters?: boolean;
};

const OrdersContent: React.FC<OrdersContentProps> = ({
	schemaType,
	buyerId,
	sellerId,
	showFilters = true,
}) => {
	const { getErrorInterface } = useApiError();

	const server = useServerTableState();

	const [filters, setFilters] = useState<OrdersFilterValues>({
		status: [],
		buyer: "",
		seller: "",
		email: "",
	});

	const cfg = useOrdersConfig({
		schemaType,
		filters,
		page: server.pageIndex + 1,
		pageSize: server.pageSize,
		buyerId,
		sellerId,
	});

	const rows = cfg.orders?.results ?? [];
	const rowSelection = useDataTableRowSelection<OrderRow>(true);

	return (
		<div className="flex flex-col gap-6 mt-4">
			{showFilters && (
				<ToolbarSwitcher
					hasSelection={rowSelection.selectedIds.length > 0}
					filterComponent={
						<OrdersFilterForm
							schemaType={schemaType}
							facetsCounts={cfg.facetsCounts}
							onSearch={(values) => {
								setFilters((prev) => ({ ...prev, ...values }));
								server.onPageChange(0);
							}}
						/>
					}
					actionComponent={
						<OrdersBulkAction
							items={rowSelection.selectedItems}
							onClear={rowSelection.clear}
						/>
					}
				/>
			)}

			<DataTable
				columns={cfg.columns}
				data={rows}
				totalItems={cfg.orders?.count ?? 0}
				server={server}
				isLoading={cfg.isLoading}
				isFetching={cfg.isFetching}
				error={cfg.error ? getErrorInterface(cfg.error) : null}
				onRetry={cfg.refetch}
				getRowId={(row) => row.pk}
				rowSelection={rowSelection}
			/>
		</div>
	);
};

export default OrdersContent;
